import { QueryDocumentSnapshot, SnapshotOptions } from "firebase/firestore";

export type ExecutionType = {
    chor: boolean;
    solo: boolean;
    orchester: boolean;
};

/** The fields of a song an admin edits in the repertoire view. */
export type ChoirSongDetails = {
    executionType: ExecutionType;
    tags: string[];
    songbookNumber: string;
    // Link to the sheet music; `sourceUrl` stays the sync source (Google Sheet).
    sheetMusicUrl: string;
    notes: string;
};

export default class ChoirSong {
    // Firestore document id (derived from the title, see utils/songTitle.ts).
    id: string = "";
    name: String;
    executionType?: ExecutionType;
    sourceUrl?: String;
    tags: string[] = [];
    songbookNumber: string = "";
    sheetMusicUrl: string = "";
    notes: string = "";
    // Hidden from the suggestions and the default repertoire list.
    archived: boolean = false;
    // Created from a service entry that was not in the catalogue; an admin confirms it.
    unverified: boolean = false;
    // Every date the song was (or is planned to be) sung, sorted; kept by a
    // Cloud Function. Missing until the choir backfill has run.
    performanceDates?: string[];

    constructor(name: String, executionType?: ExecutionType, sourceUrl?: String) {
        this.name = name;
        this.executionType = executionType;
        this.sourceUrl = sourceUrl;
    }
}

export const choirSongConverter = {
    toFirestore: (choirSong: ChoirSong) => {
        const data: Record<string, unknown> = {
            name: choirSong.name,
        };
        if (choirSong.executionType) {
            data.executionType = choirSong.executionType;
        }
        if (choirSong.sourceUrl) {
            data.sourceUrl = choirSong.sourceUrl;
        }
        return data;
    },

    fromFirestore: (snapshot: QueryDocumentSnapshot, options: SnapshotOptions) => {
        const data = snapshot.data(options);
        const song = new ChoirSong(
            data.name ?? snapshot.id,
            data.executionType,
            data.sourceUrl
        );
        song.id = snapshot.id;
        song.tags = Array.isArray(data.tags) ? data.tags.filter((tag: unknown) => typeof tag === "string") : [];
        song.songbookNumber = data.songbookNumber ? String(data.songbookNumber) : "";
        song.sheetMusicUrl = data.sheetMusicUrl ?? "";
        song.notes = data.notes ?? "";
        song.archived = data.archived === true;
        song.unverified = data.unverified === true;
        song.performanceDates = Array.isArray(data.performanceDates) ? data.performanceDates : undefined;
        return song;
    },
};
